// Thin wrapper over @capacitor/push-notifications (backlog MOBILE-11) — the
// one place that talks to the plugin, so App.tsx's bootstrap effect and
// AccountPage's Notifications panel both go through the same functions
// rather than importing the plugin directly in two places.
//
// FCM is the single send surface for both platforms (see send-notification's
// own header comment) — this file only registers for a token and reports it
// to register-push-token; it never talks to FCM/APNs directly.
import { Capacitor, type PluginListenerHandle } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { supabase } from "@/lib/supabase";

const TOKEN_STORAGE_KEY = "snapdraw.pushToken";

export type PushPermissionStatus = "granted" | "denied" | "prompt";

function normalizeStatus(receive: string): PushPermissionStatus {
  if (receive === "granted") return "granted";
  if (receive === "denied") return "denied";
  return "prompt"; // covers "prompt" and "prompt-with-rationale"
}

/** Always "denied" on web — matches this feature's native-only scope everywhere else (Paywall.tsx's own Capacitor.isNativePlatform() precedent). */
export async function checkPushPermission(): Promise<PushPermissionStatus> {
  if (!Capacitor.isNativePlatform()) return "denied";
  const status = await PushNotifications.checkPermissions();
  return normalizeStatus(status.receive);
}

/**
 * Requests the OS permission and, on grant, calls register() to start the
 * token hand-off. Resolves once requestPermissions() itself settles — the
 * real token arrives asynchronously via the 'registration' listener
 * (registerPushListeners below), not this call's return value.
 */
export async function requestAndRegisterPush(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  const status = await PushNotifications.requestPermissions();
  if (status.receive !== "granted") return false;
  await PushNotifications.register();
  return true;
}

function getStoredPushToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storePushToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    // best-effort — a lost write only means sign-out can't also revoke it
  }
}

/** Upserts this device's token server-side. Best-effort — a failure here never blocks anything the caller is doing. */
export async function registerPushToken(token: string): Promise<void> {
  storePushToken(token);
  const platform = Capacitor.getPlatform() === "ios" ? "ios" : "android";
  const { error } = await supabase.functions.invoke("register-push-token", { body: { token, platform, deviceName: null } });
  if (error) console.error("registerPushToken failed:", error);
}

/**
 * Revokes this device's own last-known token — called on sign-out (all 4
 * call sites: AccountPage, Paywall, app-shell's sidebar, deleteAccount).
 * Reads from localStorage rather than needing a live 'registration' event
 * in scope, since sign-out can happen long after the token was captured.
 * Best-effort: sign-out itself must never be blocked or fail over this.
 */
export async function revokeCurrentPushToken(): Promise<void> {
  const token = getStoredPushToken();
  if (!token) return;
  try {
    const { error } = await supabase.functions.invoke("revoke-push-token", { body: { token } });
    if (error) console.error("revokeCurrentPushToken failed:", error);
  } catch (e) {
    console.error("revokeCurrentPushToken failed:", e);
  }
}

/**
 * Revokes this device's push token, then signs out — the one order that
 * works, since revoke-push-token needs the still-live session's JWT. Used
 * at every real sign-out call site (AccountPage, Paywall, app-shell's
 * sidebar); deleteAccount() isn't one of them — its own delete-account call
 * already deletes the push_tokens row via the account's own cascade before
 * signOut() ever runs there.
 */
export async function signOutAndRevokePush(): Promise<void> {
  await revokeCurrentPushToken();
  await supabase.auth.signOut();
}

export async function markNotificationOpened(sendId: string): Promise<void> {
  try {
    const { error } = await supabase.functions.invoke("mark-notification-opened", { body: { sendId } });
    if (error) console.error("markNotificationOpened failed:", error);
  } catch (e) {
    console.error("markNotificationOpened failed:", e);
  }
}

/**
 * Registers the 3 listeners this app needs for the lifetime of the process
 * — called once from App.tsx's own bootstrap effect. `onDeepLink` is the
 * SPA-router hand-off for a tapped notification (build brief §7.9).
 * Returns a cleanup function removing all 3, matching a normal
 * `useEffect` teardown; a no-op on web.
 */
export function registerPushListeners(onDeepLink: (deepLink: string) => void): () => void {
  if (!Capacitor.isNativePlatform()) return () => {};

  const handles: Promise<PluginListenerHandle>[] = [
    PushNotifications.addListener("registration", (token) => {
      registerPushToken(token.value).catch((e) => console.error("registerPushToken failed:", e));
    }),
    PushNotifications.addListener("registrationError", (err) => {
      console.error("Push registration error:", err);
    }),
    PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      const data = (action.notification.data ?? {}) as Record<string, unknown>;
      const sendId = typeof data.notificationSendId === "string" ? data.notificationSendId : undefined;
      const deepLink = typeof data.deepLink === "string" ? data.deepLink : undefined;
      if (sendId) markNotificationOpened(sendId).catch((e) => console.error("markNotificationOpened failed:", e));
      if (deepLink) onDeepLink(deepLink);
    }),
  ];

  return () => {
    handles.forEach((h) => h.then((handle) => handle.remove()).catch(() => {}));
  };
}
